import { Button, Cell, Spacer } from '@vinted/web-ui';
import { useState, useRef } from 'react';
import { Waypoint } from 'react-waypoint';
import useTranslate from 'hooks/useTranslate';
import useBreakpoint from 'hooks/useBreakpoint';
import useTracking from 'hooks/useTracking';
import { FeaturedCollectionCheckout } from 'pages/FeaturedCollection/FeaturedCollectionCheckout';
import HorizontalScrollArea from 'components/HorizontalScrollArea';
import { ControlScrollType } from 'components/HorizontalScrollArea/HorizontalScrollArea';
import { clickEvent, impressionEvent } from 'libs/common/event-tracker/events';
import { navigateToPage } from 'libs/utils/window';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { Screen } from 'constants/tracking/screens';
import { COLLECTIONS_MANAGEMENT_URL, MEMBER_PROFILE_URL } from 'constants/routes';
import { ListItemContentType } from 'constants/tracking/content-types';
import { ContentSource } from 'constants/tracking/content-sources';
import CollectionBox from './CollectionBox';
import AddNewCollectionButton from './AddNewCollectionButton';
import useFetchCollections from './hooks/useFetchCollections';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const MultipleCollectionsBlock = _ref => {
  let {
    viewingSelf,
    userId,
    viewedCollectionId,
    nextPush
  } = _ref;
  const {
    collectionsData,
    error
  } = useFetchCollections(userId);
  const translate = useTranslate('featured_collection.multiple.collections_block');
  const breakpoints = useBreakpoint();
  const {
    track
  } = useTracking();
  const seenCollections = useRef([]);
  const {
    0: isCheckoutOpen,
    1: setIsCheckoutOpen
  } = useState(false);
  if (!collectionsData || error) return null;
  const {
    humanizedTimeLeft,
    maxActiveCount,
    maxTotalCount,
    collections
  } = collectionsData;
  const activeCollectionsCount = collections.filter(collection => collection.isActive).length;
  const collectionsWithItemsCount = collections.filter(collection => collection.itemsCount > 0).length;

  function handleManageClick() {
    track(clickEvent({
      screen: Screen.CurrentUserProfile,
      target: ClickableElement.ManageCollections
    }));
    navigateToPage(COLLECTIONS_MANAGEMENT_URL);
  }

  function handleActivateClick() {
    track(clickEvent({
      screen: Screen.CurrentUserProfile,
      target: ClickableElement.ActivateFeaturedCollections
    }));
    setIsCheckoutOpen(true);
  }

  function handleCheckoutClose() {
    setIsCheckoutOpen(false);
  }

  function handleCheckoutSuccess() {
    navigateToPage(MEMBER_PROFILE_URL(userId));
  }

  const renderTitle = () => {
    if (!viewingSelf) return translate('title');
    if (!maxActiveCount) return translate('preview_title');
    return translate('active_title', {
      count: activeCollectionsCount,
      max_count: maxActiveCount
    });
  };

  const renderSubtitle = () => {
    if (!viewingSelf) return null;
    if (!maxActiveCount) return translate('subtitle');
    return humanizedTimeLeft;
  };

  const renderManageButton = () => {
    if (!viewingSelf) return null;
    return /*#__PURE__*/_jsx(Button, {
      text: translate('actions.manage'),
      styling: Button.Styling.Flat,
      size: Button.Size.Medium,
      testId: "collections-block-manage-button",
      onClick: handleManageClick
    });
  };

  const renderCollectionsInfo = () => {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Large
      }), /*#__PURE__*/_jsx(Cell, {
        title: renderTitle(),
        body: renderSubtitle(),
        styling: Cell.Styling.Tight,
        theme: "transparent",
        suffix: renderManageButton(),
        testId: "collections-block-info"
      }), /*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Large
      })]
    });
  };

  const handleCollectionVisibility = () => {
    if (seenCollections.current.includes(userId)) return;
    track(impressionEvent({
      id: userId,
      position: 0,
      contentType: ListItemContentType.FeaturedCollection,
      contentSource: ContentSource.FeaturedCollection
    }));
    seenCollections.current.push(userId);
  };

  const renderCollectionBox = (collection, index) => {
    if (!viewingSelf && (!collection.isActive || collection.itemsCount === 0)) return null;
    return /*#__PURE__*/_jsx(HorizontalScrollArea.Item, {
      className: "multiple-collections-block__scroll-item",
      children: /*#__PURE__*/_jsx(CollectionBox, {
        collection: collection,
        viewingSelf: viewingSelf,
        index: index,
        userId: userId,
        viewedCollectionId: viewedCollectionId,
        nextPush: nextPush
      })
    }, "collection-".concat(collection.id));
  };

  const renderAddNewCollectionButton = () => {
    if (!viewingSelf || !maxTotalCount) return null;
    if (collections.length >= maxTotalCount) return null;
    return /*#__PURE__*/_jsx(HorizontalScrollArea.Item, {
      className: "multiple-collections-block__scroll-item",
      children: /*#__PURE__*/_jsx(AddNewCollectionButton, {})
    });
  };

  const renderActivateCollectionButton = () => {
    if (!viewingSelf || !!maxActiveCount) return null;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Button, {
        type: Button.Type.Button,
        text: translate('actions.activate'),
        styling: Button.Styling.Filled,
        theme: "primary",
        onClick: handleActivateClick,
        size: breakpoints.phones ? Button.Size.Medium : undefined,
        inline: !breakpoints.phones,
        testId: "collections-block-activate-button"
      }), /*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.XLarge
      }), /*#__PURE__*/_jsx(FeaturedCollectionCheckout, {
        show: isCheckoutOpen,
        onClose: handleCheckoutClose,
        onSuccess: handleCheckoutSuccess
      })]
    });
  };

  if (!collections.length && maxActiveCount === 0) return null;
  if (!viewingSelf && !activeCollectionsCount || !viewingSelf && !collectionsWithItemsCount) return null;
  return /*#__PURE__*/_jsx(Waypoint, {
    onEnter: handleCollectionVisibility,
    children: /*#__PURE__*/_jsxs("div", {
      className: "multiple-collections-block",
      children: [/*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Large
      }), renderCollectionsInfo(), /*#__PURE__*/_jsxs(HorizontalScrollArea, {
        controlsScrollType: ControlScrollType.Partial,
        children: [collections.map(renderCollectionBox), renderAddNewCollectionButton()]
      }), /*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Large
      }), renderActivateCollectionButton()]
    })
  });
};

export default MultipleCollectionsBlock;